// src/pages/lkp/EntityBindings.tsx
// Step 1 layer for an entity: the fields bound to it, plus bind / merge / split controls.
import React, { useState } from "react";
import type { LookupEntity } from "../api/lkpEntityAPI";
import { Section, type EntityAct } from "./entityShared";
import type { BindingCert } from "./LookupEntityWorkspace";

const upFid = (s?: string) => (s || "").toUpperCase();

/** The "Bound fields" section (fields that share this reference domain). */
export function BoundFieldsSection({ entity: e, busy, act, cert, onGoStep }:
  { entity: LookupEntity; busy: string | null; act: EntityAct;
    cert?: Map<string, BindingCert>; onGoStep?: (n: 1 | 2) => void }) {
  return (
    <Section title="Bound fields" hint="Fields that share this reference domain. Each shows whether Step 1 (profiling) and Step 2 (client) certified it.">
      <div className="bind-rows">
        {e.bindings.map((b) => {
          const c = cert?.get(upFid(b.field_id));
          const certified = !c || c.certified;               // if unknown, don't nag
          return (
            <div key={b.field_id} className={"bind" + (c && !c.certified ? " bind-attn" : "")}>
              <span className="mono b-fid">{b.field_id}</span>
              {b.business_name && <span className="b-bn">{b.business_name}</span>}
              <span className="b-src">{b.source}</span>
              {c && (c.s1 || c.s2)
                ? <span className="b-cert ok">{c.s1 && c.s2 ? "✓ Step 1 & 2" : c.s1 ? "✓ Step 1" : "✓ Step 2"}</span>
                : c ? <span className="b-cert no">⚠ not accepted</span> : null}
              {b.confidence != null && <span className="b-conf">conf {b.confidence}</span>}
              <div className="sp" />
              {c && !certified && onGoStep && (
                <span className="b-cta">
                  <button className="mini" disabled={!!busy} onClick={() => onGoStep(1)}>Accept in Step 1</button>
                  <button className="mini" disabled={!!busy} onClick={() => onGoStep(2)}>Step 2</button>
                </span>
              )}
              <button className="mini" disabled={!!busy} onClick={() => act("unbind", { field_id: b.field_id })}>Unbind</button>
            </div>
          );
        })}
        <BindControl act={act} busy={!!busy} />
      </div>
      <style>{`
        .bind-attn{background:var(--wait-bg);border-radius:8px;padding:6px 8px}
        .b-cert{font-size:10.5px;font-weight:600;padding:2px 7px;border-radius:5px}
        .b-cert.ok{background:var(--gov-bg);color:var(--gov)} .b-cert.no{background:var(--warn-bg);color:var(--warn)}
        .b-cta{display:inline-flex;gap:5px;margin-right:6px}
      `}</style>
    </Section>
  );
}

export function BindControl({ act, busy }: { act: EntityAct; busy: boolean }) {
  const [v, setV] = useState("");
  const apply = () => { const [t, c] = v.split("."); if (t && c) { act("bind", { table: t.trim(), column: c.trim() }); setV(""); } };
  return (
    <div className="bind add">
      <input className="mono" placeholder="TABLE.COLUMN" value={v} onChange={(e) => setV(e.target.value)} />
      <button className="mini pri" disabled={busy || !v.includes(".")} onClick={apply}>Bind field</button>
    </div>
  );
}

export function MergeControl({ e, all, act, busy }:
  { e: LookupEntity; all: LookupEntity[]; act: EntityAct; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const others = all.filter((x) => x.entity_id !== e.entity_id);
  if (!others.length) return null;
  return (
    <div className="merge">
      <button className="btn line" disabled={busy} onClick={() => setOpen((o) => !o)}>Merge…</button>
      {open && (
        <div className="merge-pop">
          <div className="mp-h">Merge another entity INTO “{e.name}”</div>
          {others.map((o) => (
            <button key={o.entity_id} className="mp-item" disabled={busy}
              onClick={() => { act("merge", { source_ids: [o.entity_id] }); setOpen(false); }}>{o.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SplitControl({ e, act, busy }: { e: LookupEntity; act: EntityAct; busy: boolean }) {
  const [sel, setSel] = useState<string[]>([]);
  const toggle = (fid: string) => setSel((s) => s.includes(fid) ? s.filter((x) => x !== fid) : [...s, fid]);
  return (
    <div className="split">
      {e.bindings.map((b) => (
        <label key={b.field_id} className="split-f">
          <input type="checkbox" checked={sel.includes(b.field_id)} onChange={() => toggle(b.field_id)} />{b.field_id}
        </label>
      ))}
      <button className="mini" disabled={busy || !sel.length}
        onClick={() => { const n = prompt("New entity name for the split-off fields"); if (n) act("split", { field_ids: sel, new_name: n }); }}>Split selected →</button>
    </div>
  );
}
